// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 2.0
// updated  : 2026-06-21
// protected: NO
// changelog: v2.0 (UAT-02 Fase 2) - Request->Dispatch. Kacab ajukan request (Merchandize+
//            Size+Qty, TANPA QR). Admin Gudang scan QR (exact-match, Jual only) -> keluar.
//            Kacab konfirmasi. Compliment tidak masuk omzet.
// =============================================================================
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/v2/components/statusMap";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Gift, Loader2, Send, CheckCircle2, XCircle, Check, Clock, FileText, ScanLine, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { QrBatchInput } from "@/v2/components/QrBatchInput";
import { RequestLineEditor, RequestLinesSummary } from "@/v2/components/RequestLineEditor";
import { isOwner, openPdf } from "@/v2/components/helpers";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const KACAB = ["KACAB_SBY", "KACAB_SMD"];
const GUDANG = ["ADMIN_GUDANG_SBY", "ADMIN_GUDANG_SMD"];
const STEPS = ["Diajukan", "Stok Keluar", "Selesai"];
const STATUS_STEP = { REQUESTED: 0, PREPARING: 0, DISPATCHED: 1, COMPLETED: 2 };
const STATUS_LABEL = {
  REQUESTED: "Diajukan", PREPARING: "Disiapkan", DISPATCHED: "Dikeluarkan",
  COMPLETED: "Selesai", CANCELLED: "Dibatalkan",
};

const Stepper = ({ status }) => {
  if (status === "CANCELLED") {
    return <Badge variant="danger" data-testid="cmp-step-cancelled">Dibatalkan</Badge>;
  }
  const cur = STATUS_STEP[status] ?? 0;
  return (
    <div className="flex items-center gap-1.5" data-testid="cmp-stepper">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-1.5">
            <span className={`grid place-items-center w-5 h-5 rounded-full text-[10px] font-semibold ${
              i <= cur ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              {i < cur ? <Check className="w-3 h-3" /> : i + 1}
            </span>
            <span className={`text-xs ${i <= cur ? "font-medium" : "text-muted-foreground"}`}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <span className={`w-4 h-px ${i < cur ? "bg-primary" : "bg-border"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function ComplimentPage() {
  const { api, me } = useV2Auth();
  const role = me?.role;
  const isKacab = KACAB.includes(role);
  const isGudang = GUDANG.includes(role);
  const owner = isOwner(role);

  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("compliments"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const listTopRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);
  // form (Kacab)
  const [reqLines, setReqLines] = useState([]);
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [formKey, setFormKey] = useState(0);
  // dispatch dialog (Admin Gudang)
  const [dispatchDoc, setDispatchDoc] = useState(null);
  const [scanQrs, setScanQrs] = useState([]);
  // cancel dialog
  const [cancelDoc, setCancelDoc] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const load = useCallback(async (p = 1, size = pageSize) => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/compliments?page=${p}&page_size=${size}`);
      const data = await r.json().catch(() => ({}));
      if (r.ok) { setList(data.items || []); setMeta({ total: data.total || 0, pages: data.pages || 1 }); setPage(data.page || p); }
      else setList([]);
    } finally { setLoading(false); }
  }, [api, pageSize]);
  const goPage = (p) => { load(p); if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const changePageSize = (s) => { setPageSize(s); load(1, s); };

  const loadProducts = useCallback(async () => {
    const r = await api("/api/v2/merchandise/products");
    if (r.ok) { const d = await r.json(); setProducts(d.items || []); }
  }, [api]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isKacab) loadProducts(); }, [isKacab, loadProducts]);

  const openDetail = async (id) => {
    const r = await api(`/api/v2/compliments/${id}`);
    if (r.ok) setDetail(await r.json());
  };

  const totalReqQty = reqLines.reduce((s, l) => s + Number(l.qty || 0), 0);

  const create = async () => {
    if (!reqLines.length) { toast.error("Tambahkan minimal 1 Merchandize dengan Quantity > 0"); return; }
    if (!recipient.trim()) { toast.error("Penerima compliment wajib diisi"); return; }
    setCreating(true);
    try {
      const r = await api("/api/v2/compliments", {
        method: "POST", body: JSON.stringify({ request_lines: reqLines, recipient, note }) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal menyimpan request"); return; }
      toast.success(`Request ${d.request_no} dibuat (${d.request_count} pcs)`);
      setReqLines([]); setRecipient(""); setNote(""); setFormKey((k) => k + 1);
      await load();
    } finally { setCreating(false); }
  };

  const openDispatch = (doc) => { setScanQrs([]); setDispatchDoc(doc); };

  const submitDispatch = async () => {
    if (!dispatchDoc) return;
    if (scanQrs.length === 0) { toast.error("Scan QR sesuai request terlebih dahulu"); return; }
    setBusyId(dispatchDoc.id);
    try {
      const r = await api(`/api/v2/compliments/${dispatchDoc.id}/dispatch`, {
        method: "POST", body: JSON.stringify({ qr_ids: scanQrs }) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal konfirmasi pengeluaran"); return; }
      toast.success(`Stok keluar · ${d.result?.ok_count || 0} item`);
      setDispatchDoc(null); setScanQrs([]); await load();
    } finally { setBusyId(null); }
  };

  const act = async (doc, action, body) => {
    setBusyId(doc.id);
    try {
      const r = await api(`/api/v2/compliments/${doc.id}/${action}`, {
        method: "POST", body: JSON.stringify(body || {}) });
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memproses"); return false; }
      toast.success(`Berhasil: ${STATUS_LABEL[d.status]}`);
      await load();
      if (detail?.id === doc.id) await openDetail(doc.id);
      return true;
    } finally { setBusyId(null); }
  };

  const doCancel = async () => {
    const ok = await act(cancelDoc, "cancel", { reason: cancelReason });
    if (ok) { setCancelDoc(null); setCancelReason(""); }
  };

  const renderActions = (doc) => {
    const busy = busyId === doc.id;
    const btns = [];
    if (isGudang && (doc.status === "REQUESTED" || doc.status === "PREPARING"))
      btns.push(<Button key="disp" size="sm" disabled={busy} onClick={() => openDispatch(doc)} data-testid="cmp-open-dispatch"><ScanLine className="w-4 h-4 mr-1" />Scan &amp; Keluarkan</Button>);
    if (isKacab && doc.status === "DISPATCHED")
      btns.push(<Button key="conf" size="sm" disabled={busy} onClick={() => act(doc, "confirm")} data-testid="cmp-confirm-btn"><CheckCircle2 className="w-4 h-4 mr-1" />Konfirmasi Terima</Button>);
    if (isKacab && (doc.status === "REQUESTED" || doc.status === "PREPARING"))
      btns.push(<Button key="cancel" size="sm" variant="outline" disabled={busy} onClick={() => setCancelDoc(doc)} data-testid="cmp-cancel-btn"><XCircle className="w-4 h-4 mr-1" />Batal</Button>);
    return btns;
  };

  return (
    <div className="space-y-5" data-testid="v2-compliment-page">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <Gift className="w-6 h-6 text-primary" /> Compliment (Kepala Cabang)
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Kacab ajukan (Merchandize + Size + Qty, tanpa QR) → Admin Gudang scan QR keluar (sesuai persis) → Kacab konfirmasi. Compliment tidak masuk omzet.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        {/* Form pengajuan (Kacab) */}
        {isKacab ? (
          <Card className="p-5 rounded-2xl lg:col-span-2 space-y-3 h-fit" data-testid="cmp-create-card">
            <h3 className="font-semibold">Ajukan Compliment Baru</h3>
            <div>
              <Label className="text-xs">Penerima compliment *</Label>
              <Input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="cth: Sponsor / Tamu VIP" data-testid="cmp-recipient" />
            </div>
            <div>
              <Label className="text-xs">Baris Pengajuan (JUAL saja)</Label>
              <div className="mt-1">
                <RequestLineEditor key={formKey} products={products} allowMou={false} onLinesChange={setReqLines} testid="cmp-lines" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Keterangan</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Alasan / catatan" data-testid="cmp-note" />
            </div>
            <div className="flex items-center justify-between">
              <Badge variant="outline" data-testid="cmp-total">{totalReqQty} pcs</Badge>
              <Button onClick={create} disabled={creating} data-testid="cmp-submit">
                {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Gift className="w-4 h-4 mr-1" />}Ajukan Request
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-5 rounded-2xl lg:col-span-2 grid place-items-center text-center text-sm text-muted-foreground min-h-[140px]" data-testid="cmp-role-note">
            {owner
              ? "Mode Owner: read-only (audit). Pengajuan/aksi compliment dinonaktifkan."
              : isGudang
                ? "Sebagai Admin Gudang: scan QR (sesuai persis request) untuk keluarkan Merchandize."
                : "Tidak ada aksi pengajuan untuk role Anda."}
          </Card>
        )}

        {/* Daftar request */}
        <Card className="p-5 rounded-2xl lg:col-span-3" data-testid="cmp-list-card" ref={listTopRef}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Daftar Request</h3>
            <Badge variant="outline">{meta.total}</Badge>
          </div>
          {loading && list.length === 0 && <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
          <div className="space-y-3">
            {list.map((doc) => (
              <div key={doc.id} className="border rounded-xl p-3 space-y-2" data-testid="cmp-row">
                <div className="flex items-center justify-between gap-2">
                  <button className="font-mono text-sm font-medium hover:underline" onClick={() => openDetail(doc.id)} data-testid="cmp-open-detail">
                    {doc.request_no}
                  </button>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openPdf(api, "compliment", doc.id, doc.request_no)} data-testid="cmp-pdf" title="Unduh PDF"><FileText className="w-4 h-4" /></Button>
                    <StatusBadge status={doc.status} label={STATUS_LABEL[doc.status]} testid="cmp-status" />
                  </div>
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {doc.warehouse} · untuk <b className="text-foreground">{doc.recipient}</b> · {doc.request_count || 0} pcs
                </div>
                {doc.request_lines?.length > 0 && <RequestLinesSummary lines={doc.request_lines} testid="cmp-reqsum" />}
                <Stepper status={doc.status} />
                {!owner && <div className="flex flex-wrap gap-2 pt-1">{renderActions(doc)}</div>}
              </div>
            ))}
          </div>
          <ListPager
            page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={loading}
            onPageChange={goPage} onPageSizeChange={changePageSize}
            storageKey="compliments" testid="compliment-pager"
          />
        </Card>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="cmp-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.request_no} · {STATUS_LABEL[detail?.status]}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <Stepper status={detail.status} />
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">Gudang:</span> {detail.warehouse}</div>
                <div><span className="text-muted-foreground">Penerima:</span> {detail.recipient}</div>
              </div>
              {detail.note && <div><span className="text-muted-foreground">Keterangan:</span> {detail.note}</div>}
              {detail.cancel_reason && <div className="text-destructive">Alasan batal: {detail.cancel_reason}</div>}
              <Separator />
              <div className="space-y-1">
                <Label className="text-xs">Request (diajukan)</Label>
                <RequestLinesSummary lines={detail.request_lines || []} testid="cmp-detail-req" />
              </div>
              {detail.lines?.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">QR keluar ({detail.lines.length})</Label>
                  {detail.lines.map((l) => (
                    <div key={l.id} className="flex items-center justify-between border rounded-lg px-3 py-1.5" data-testid="cmp-detail-line">
                      <span className="font-mono text-xs">{l.qr_id} · {l.club} {l.size}</span>
                      {l.dispatched
                        ? <Badge variant="success">keluar</Badge>
                        : l.dispatch_error
                          ? <Badge variant="destructive">{l.dispatch_error}</Badge>
                          : <Badge variant="outline">menunggu</Badge>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dispatch dialog (Admin Gudang scan QR) */}
      <Dialog open={!!dispatchDoc} onOpenChange={(o) => !o && setDispatchDoc(null)}>
        <DialogContent data-testid="cmp-dispatch-dialog">
          <DialogHeader><DialogTitle>Scan &amp; Keluarkan · {dispatchDoc?.request_no}</DialogTitle></DialogHeader>
          {dispatchDoc && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">Target Request (harus dipenuhi PERSIS)</Label>
                <div className="mt-1"><RequestLinesSummary lines={dispatchDoc.request_lines || []} testid="cmp-dispatch-target" /></div>
                <div className="text-xs text-muted-foreground mt-1">Total diminta: <b>{dispatchDoc.request_count}</b> pcs · untuk {dispatchDoc.recipient}</div>
              </div>
              <div>
                <Label className="text-xs">Scan QR fisik (gudang {dispatchDoc.warehouse})</Label>
                <div className="mt-1"><QrBatchInput value={scanQrs} onChange={setScanQrs} testid="cmp-dispatch-qr" /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-muted px-3 py-2">
                <span className="text-xs">Discan</span>
                <Badge variant={scanQrs.length === dispatchDoc.request_count ? "default" : "secondary"} data-testid="cmp-dispatch-count">
                  {scanQrs.length} / {dispatchDoc.request_count}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground">Compliment hanya JUAL. Jika tidak sesuai request, konfirmasi pengeluaran ditolak (tanpa partial).</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispatchDoc(null)}>Tutup</Button>
            <Button onClick={submitDispatch} disabled={busyId === dispatchDoc?.id} data-testid="cmp-dispatch-submit">
              {busyId === dispatchDoc?.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Konfirmasi Pengeluaran
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelDoc} onOpenChange={(o) => !o && setCancelDoc(null)}>
        <DialogContent data-testid="cmp-cancel-dialog">
          <DialogHeader><DialogTitle>Batalkan {cancelDoc?.request_no}?</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Alasan pembatalan</Label>
            <Textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cmp-cancel-reason" />
            <p className="text-xs text-muted-foreground">Hanya bisa dibatalkan sebelum barang keluar (Dikeluarkan).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDoc(null)}>Tutup</Button>
            <Button variant="destructive" onClick={doCancel} disabled={busyId === cancelDoc?.id} data-testid="cmp-cancel-confirm">Batalkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
